import { useState } from 'react';
import { InboundProcess } from '@features/supplyChain/types';
import dayjs from 'dayjs';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/20/solid';
import { ClockIcon } from '@heroicons/react/24/outline';
import { getNoticeColors } from '@views/Noticeboard/utils';
import { getTotalPallets } from '@utils';
import { useSetAtom } from 'jotai';
import { SkuInfoLink } from '@tailwindComponents/SkuInfoModal';
import { selectedShipmentNoteNumberAtom } from '../../../store/jotai';
import { ShipmentStatusBadge } from './ShipmentStatusBadge';

interface CollapsibleInboundRowProps {
  inboundProcess: InboundProcess;
  selected: boolean;
  onSelectChange: (id: string, checked: boolean) => void;
  onOpenProcess: (inboundProcess: InboundProcess) => void;
}

export function CollapsibleInboundRow({
  inboundProcess,
  selected,
  onSelectChange,
  onOpenProcess,
}: CollapsibleInboundRowProps) {
  const [open, setOpen] = useState(false);
  const setSelectedShipmentNoteNumber = useSetAtom(
    selectedShipmentNoteNumberAtom
  );

  const { notices, goodsReceivedNote } = inboundProcess;

  const hasIssues = (notices?.length ?? 0) > 0;
  const isProcessComplete = !!goodsReceivedNote?.finalisedAt;
  const hasIssuesBeforeProcessIsComplete = hasIssues && !isProcessComplete;
  const hasIssuesWhenProcessIsComplete = hasIssues && isProcessComplete;
  const hasNoIssuesWhenProcessIsComplete = !hasIssues && isProcessComplete;

  const totalSkuQuantity = inboundProcess.shipmentNote.skus.reduce(
    (prev, current) => prev + current.skuQuantity,
    0
  );

  // Atomic products (e.g. bottles) = Σ skuQuantity × amountOfProductInSku.
  const totalProducts = inboundProcess.shipmentNote.skus.reduce(
    (prev, current) =>
      prev + current.skuQuantity * (current.amountOfProductInSku || 1),
    0
  );
  const showProducts = totalProducts !== totalSkuQuantity;
  // Lead with the SKUs' real unit (e.g. "cases"); fall back to "sku" for mixed/Other.
  const skuUnits = inboundProcess.shipmentNote.skus
    .map((sku) => sku.unitOfMeasure)
    .filter(Boolean);
  const unitsWord =
    skuUnits.length > 0 &&
    skuUnits.every((u) => u === skuUnits[0]) &&
    (skuUnits[0] || '').toLowerCase() !== 'other'
      ? (skuUnits[0] || '').toLowerCase()
      : 'unit';

  const totalPallets = inboundProcess.shipmentNote.skus
    .map((sku) => {
      return getTotalPallets(
        sku.skuQuantity,
        sku.packagingDetails.packagingInfo.amountOfSkusInLayer,
        sku.packagingDetails.packagingInfo.amountOfLayersInPallet
      );
    })
    .reduce((prev, current) => prev + current, 0);

  return (
    <div className='border-t border-gray-200'>
      <div className='flex items-start gap-5 border-b border-gray-200 p-5'>
        {/* Expand/Collapse chevron */}
        <button
          type='button'
          aria-label='expand row'
          onClick={() => setOpen(!open)}
          className='shrink-0 text-gray-400 hover:text-gray-600'
        >
          {open ? (
            <ChevronDownIcon className='h-6 w-6' />
          ) : (
            <ChevronRightIcon className='h-6 w-6' />
          )}
        </button>

        {/* Order Pool - Status badge */}
        <div className='flex flex-1 flex-col items-center justify-center gap-2.5 max-w-[160px] min-w-[120px] px-2.5'>
          <button
            type='button'
            aria-label='manage shipment'
            onClick={() => onOpenProcess(inboundProcess)}
            className='rounded-full focus:outline-none focus:ring-2 focus:ring-blue-200'
          >
            <ShipmentStatusBadge status={inboundProcess.status} />
          </button>
          {open && (
            <button
              type='button'
              onClick={() =>
                setSelectedShipmentNoteNumber(
                  inboundProcess.shipmentNote.shipmentNoteNumber
                )
              }
              className='flex items-center text-sm text-gray-950 hover:text-blue-600'
            >
              <span>id</span>
              <span className='text-base leading-4 w-4 text-center'>#</span>
              <span>
                {inboundProcess.shipmentNote.shipmentNoteNumber.slice(-7)}
              </span>
              {hasIssuesBeforeProcessIsComplete && (
                <ExclamationCircleIcon
                  className='ml-1 h-4 w-4'
                  style={{ color: getNoticeColors('warning').fill }}
                />
              )}
              {hasIssuesWhenProcessIsComplete && (
                <ExclamationCircleIcon
                  className='ml-1 h-4 w-4'
                  style={{ color: getNoticeColors('error').fill }}
                />
              )}
              {hasNoIssuesWhenProcessIsComplete && (
                <CheckCircleIcon className='ml-1 h-4 w-4 text-green-500' />
              )}
            </button>
          )}
        </div>

        {/* Note ID */}
        <div className='flex flex-1 flex-col gap-1.5 min-w-[100px]'>
          <button
            type='button'
            onClick={() =>
              setSelectedShipmentNoteNumber(
                inboundProcess.shipmentNote.shipmentNoteNumber
              )
            }
            className='text-sm font-normal leading-5 text-blue-600 hover:text-blue-800 truncate text-left'
          >
            {inboundProcess.shipmentNote.shipmentNoteNumber}
          </button>
        </div>

        {/* From (supplier) */}
        <div className='flex flex-1 flex-col gap-1.5 min-w-[100px]'>
          <p className='text-sm font-normal leading-5 text-gray-950 truncate'>
            {inboundProcess.shipmentNote.supplier.name}
          </p>
          {open && (
            <div className='flex flex-col gap-1.5 pt-1.5'>
              <div className='flex items-center gap-1'>
                <ClockIcon className='h-4 w-4 shrink-0 text-gray-500' />
                <span className='text-sm font-semibold text-gray-950'>
                  Created
                </span>
              </div>
              <div className='flex flex-wrap items-center gap-0.5'>
                <span className='text-sm text-gray-950'>
                  {dayjs(inboundProcess.shipmentNote.createdAt).format(
                    'DD/MM/YYYY'
                  )}
                </span>
                <span className='mx-0.5 inline-block h-1 w-1 rounded-full bg-gray-950' />
                <span className='text-sm text-gray-950'>
                  {dayjs(inboundProcess.shipmentNote.createdAt).format('HH:mm')}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* To (warehouse) */}
        <div className='flex flex-1 flex-col gap-1.5 min-w-[100px]'>
          <p className='text-sm font-normal leading-5 text-gray-950 truncate'>
            {inboundProcess.shipmentNote.warehouseAddress || '-'}
          </p>
          {open && (
            <div className='flex flex-col gap-1.5 pt-1.5'>
              <p className='text-sm font-semibold text-gray-950'>
                {inboundProcess.shipmentNote.warehouseName}
              </p>
              <div className='flex items-center gap-1'>
                <ClockIcon className='h-4 w-4 shrink-0 text-gray-500' />
                <span className='text-sm font-semibold text-gray-950'>RTA</span>
              </div>
              <div className='flex flex-wrap items-center gap-0.5'>
                <span className='text-sm text-gray-950'>
                  {dayjs(
                    inboundProcess.shipmentNote.estimatedTimeOfArrival.dateTime
                  ).format('DD/MM/YYYY')}
                </span>
                <span className='mx-0.5 inline-block h-1 w-1 rounded-full bg-gray-950' />
                <span className='text-sm text-gray-950'>
                  {dayjs(
                    inboundProcess.shipmentNote.estimatedTimeOfArrival.dateTime
                  ).format('HH:mm')}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* SKUs */}
        <div className='flex flex-1 flex-col gap-1.5 max-w-[170px] min-w-[90px]'>
          {showProducts ? (
            <div className='leading-tight'>
              <p className='text-sm font-medium text-gray-900'>
                {totalProducts.toLocaleString()}
                <span className='ml-1 text-xs font-normal text-gray-500'>
                  products
                </span>
              </p>
              <p className='text-xs text-gray-500'>
                {totalSkuQuantity.toLocaleString()} {unitsWord}
                {totalSkuQuantity === 1 ? '' : 's'}
              </p>
            </div>
          ) : (
            <p className='text-sm font-normal leading-5 text-gray-900'>
              {totalSkuQuantity.toLocaleString()}
            </p>
          )}
          {open && (
            <div className='flex flex-col gap-2 pt-1.5'>
              {inboundProcess.shipmentNote.skus.map((sku) => {
                const products =
                  sku.skuQuantity * (sku.amountOfProductInSku || 1);
                const unit = (sku.unitOfMeasure || 'unit').toLowerCase();
                return (
                  <div
                    key={sku.skuId}
                    className='flex min-h-[2.25rem] flex-col leading-tight'
                  >
                    <p className='text-sm text-gray-950 truncate'>
                      <SkuInfoLink inventorySkuId={sku.skuId}>
                        {sku.skuIdentifier}
                      </SkuInfoLink>
                    </p>
                    <p className='text-xs text-gray-500'>
                      {(sku.amountOfProductInSku || 1) > 1 &&
                        `${products.toLocaleString()} products · `}
                      {sku.skuQuantity.toLocaleString()} {unit}
                      {sku.skuQuantity === 1 ? '' : 's'}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Pallets */}
        <div className='flex flex-1 flex-col gap-1.5 max-w-[150px] min-w-[74px]'>
          <p className='text-sm font-normal leading-5 text-gray-900'>
            {totalPallets}
          </p>
          {open && (
            <div className='flex flex-col gap-2 pt-1.5'>
              {inboundProcess.shipmentNote.skus.map((sku) => {
                const pallets = getTotalPallets(
                  sku.skuQuantity,
                  sku.packagingDetails.packagingInfo.amountOfSkusInLayer,
                  sku.packagingDetails.packagingInfo.amountOfLayersInPallet
                );
                return (
                  <p
                    key={sku.skuIdentifier}
                    className='flex min-h-[2.25rem] items-start text-sm text-gray-900'
                  >
                    {pallets} {sku.packagingDetails.packagingInfo.type}
                    {pallets > 1 ? 's' : ''}
                  </p>
                );
              })}
            </div>
          )}
        </div>

        {/* Checkbox */}
        <div className='shrink-0'>
          <input
            type='checkbox'
            checked={selected}
            onChange={(e) =>
              onSelectChange(inboundProcess.id, e.target.checked)
            }
            className='h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500'
          />
        </div>
      </div>
    </div>
  );
}
